import {Component, Input, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ModalController} from '@ionic/angular';
import {
    BaseModalComponent,
    Column,
    ColumnField,
    ColumnFormats,
    Filter,
    getReportFieldDisplayName,
    isNumberFormat,
    Menu,
    Organization,
    OrganizationService,
    ReportColumns,
    TaxColumns
} from 'brewbill-lib';
import {EditTabReportFilterComponent} from '../edit-tab-report-filter/edit-tab-report-filter.component';

@Component({
    selector: 'bb-edit-tab-report-column',
    templateUrl: './edit-tab-report-column.component.html',
    styleUrls: ['./edit-tab-report-column.component.scss'],
})
export class EditTabReportColumnComponent extends BaseModalComponent implements OnInit {
    @Input() column: Column;
    @Input() menu: Menu;
    @Input() requireAggregate = false;
    @Input() requireNumeric = false;
    @Input() restrictAggregate = false;
    @Input() hidePercent = false;
    @Input() showSummaryAggregate = false;
    @Input() hideRowSummary = false;
    @Input() showFromTotal = false;

    formGroup: FormGroup;
    filters: Filter[];
    organization: Organization;

    loaded = false;
    selectedColumnField: ColumnField;

    isNumberFormat = isNumberFormat;
    columnFormats = ColumnFormats;

    protected readonly getReportFieldDisplayName = getReportFieldDisplayName;

    constructor(
        private organizationService: OrganizationService,
        private formBuilder: FormBuilder,
        modalController: ModalController
    ) {
        super(modalController);
    }

    ngOnInit() {
        this.organization = this.organizationService.currentValue;
        this.filters = !!this.column && !!this.column.filters ? [...this.column.filters] : [];

        this.formGroup = this.formBuilder.group({
            id: !!this.column ? this.column.id : null,
            label: [!!this.column ? this.column.label : '', Validators.required],
            rowLabel: !!this.column ? this.column.rowLabel : false,
            filter: '',
            includeEmpty: !!this.column ? this.column.includeEmpty : false,
            key: ['', Validators.required],
            aggregation: !!this.column ? this.column.aggregation : null,
            summaryAggregation: !!this.column ? this.column.summaryAggregation : null,
            columnOne: !!this.column ? this.column.columnOne : null,
            columnTwo: !!this.column ? this.column.columnTwo : null,
            fromTotal: !!this.column && this.column.fromTotal
        });

        if (this.requireAggregate) {
            this.formGroup.controls.aggregation.setValidators([Validators.required]);
        }

        this.loaded = true;
    }

    fieldChange(columnField: ColumnField) {
        if (!!columnField && columnField.key !== this.formGroup.controls.key.value) {
            const loadingFirst = !this.formGroup.controls.key.value
                && !!this.formGroup.controls.label.value;
            this.formGroup.controls.key.setValue(columnField.key);
            this.selectedColumnField = columnField;
            if (!loadingFirst) {
                this.formGroup.controls.label.setValue(columnField.display);
                this.formGroup.controls.includeEmpty.setValue(false);
            }

            if (columnField.key.startsWith('MATH_')) {
                this.formGroup.controls.columnOne.setValidators([Validators.required]);
                this.formGroup.controls.columnTwo.setValidators([Validators.required]);
                this.formGroup.controls.aggregation.setValue('CALCULATE');

            } else {
                this.formGroup.controls.columnOne.setValue(null);
                this.formGroup.controls.columnTwo.setValue(null);
                this.formGroup.controls.columnOne.setValidators([]);
                this.formGroup.controls.columnTwo.setValidators([]);

                if (this.requireCount()) {
                    this.formGroup.controls.aggregation.setValue('COUNT');
                } else if (this.formGroup.controls.aggregation.value === 'CALCULATE') {
                    this.formGroup.controls.aggregation.setValue('');
                }

            }
            this.formGroup.controls.columnOne.updateValueAndValidity();
            this.formGroup.controls.columnTwo.updateValueAndValidity();
        }
    }

    async editColumn(columnNumber: number) {
        const column = columnNumber === 1
            ? this.formGroup.controls.columnOne.value as Column
            : this.formGroup.controls.columnTwo.value as Column;

        const modal = await this.modalController.create({
            component: EditTabReportColumnComponent,
            componentProps: {
                column,
                menu: this.menu,
                showSummaryAggregate: false,
                hidePercent: true,
                hideRowSummary: true,
                requireNumeric: true,
                showFromTotal: true
            },
            cssClass: 'report-modal'
        });
        await modal.present();

        modal.onDidDismiss().then((dataReturned) => {
            if (!!dataReturned && !!dataReturned.data) {
                if (columnNumber === 1) {
                    this.formGroup.controls.columnOne.setValue(dataReturned.data);
                } else {
                    this.formGroup.controls.columnTwo.setValue(dataReturned.data);
                }
            }
        });
    }

    async editFilter(filter: Filter) {
        const modal = await this.modalController.create({
            component: EditTabReportFilterComponent,
            componentProps: {
                filter,
                menu: this.menu,
                organization: this.organization
            },
            cssClass: 'report-modal'
        });
        await modal.present();

        modal.onDidDismiss().then((dataReturned) => {
            if (!!dataReturned && !!dataReturned.data) {
                if (!!filter) {
                    this.filters[this.filters.indexOf(filter)] = dataReturned.data;
                } else {
                    this.filters.push(dataReturned.data);
                }
            }
        });
    }

    removeFilter(event, filter) {
        event.stopPropagation();
        this.filters.splice(this.filters.indexOf(filter), 1);
    }

    async onSubmit() {
        if (this.formGroup.valid) {
            const column = new Column(this.formGroup.value);
            column.filters = !!column.aggregation ? this.filters : [];

            column.aggregation = !!column.aggregation ? column.aggregation : null;
            column.summaryAggregation = !!column.summaryAggregation ? column.summaryAggregation : null;

            const field = ReportColumns.find(c => c.key === column.key);
            const formKey = column.key;

            if (!!field) {
                column.format = field.format;
            } else {
                if (column.key.startsWith('MATH_')) {
                    if (column.key === 'MATH_PERCENTAGE') {
                        column.format = ColumnFormats.PERCENT;
                    } else {
                        column.format = !!column.columnOne
                        && !!column.columnTwo
                        && (column.columnOne.format === ColumnFormats.CURRENCY || column.columnTwo.format === ColumnFormats.CURRENCY)
                            ? ColumnFormats.CURRENCY : ColumnFormats.NUMBER;
                    }
                } else if (column.key.endsWith(TaxColumns.ORDER_ITEM_TAX_RATE)) {
                    column.keyValue = column.key.substring(0, column.key.length - TaxColumns.ORDER_ITEM_TAX_RATE.length - 1);
                    column.key = TaxColumns.ORDER_ITEM_TAX_RATE;
                    column.format = ColumnFormats.NUMBER;
                } else if (column.key.endsWith(TaxColumns.ORDER_ITEM_TAX_INCLUDED)) {
                    column.keyValue = column.key.substring(0, column.key.length - TaxColumns.ORDER_ITEM_TAX_INCLUDED.length - 1);
                    column.key = TaxColumns.ORDER_ITEM_TAX_INCLUDED;
                    column.format = ColumnFormats.BOOLEAN;
                } else if (column.key.endsWith(TaxColumns.ORDER_ITEM_TAXED_TOTAL)) {
                    column.keyValue = column.key.substring(0, column.key.length - TaxColumns.ORDER_ITEM_TAXED_TOTAL.length - 1);
                    column.key = TaxColumns.ORDER_ITEM_TAXED_TOTAL;
                    column.format = ColumnFormats.CURRENCY;
                } else if (column.key.endsWith(TaxColumns.ORDER_ITEM_TAX)) {
                    column.keyValue = column.key.substring(0, column.key.length - TaxColumns.ORDER_ITEM_TAX.length - 1);
                    column.key = TaxColumns.ORDER_ITEM_TAX;
                    column.format = ColumnFormats.CURRENCY;
                } else if (column.key.endsWith(TaxColumns.TAX_RATE)) {
                    column.keyValue = column.key.substring(0, column.key.length - TaxColumns.TAX_RATE.length - 1);
                    column.key = TaxColumns.TAX_RATE;
                    column.format = ColumnFormats.NUMBER;
                } else if (column.key.endsWith(TaxColumns.TAX_INCLUDED)) {
                    column.keyValue = column.key.substring(0, column.key.length - TaxColumns.TAX_INCLUDED.length - 1);
                    column.key = TaxColumns.TAX_INCLUDED;
                    column.format = ColumnFormats.BOOLEAN;
                } else if (column.key.endsWith(TaxColumns.TAXED_TOTAL)) {
                    column.keyValue = column.key.substring(0, column.key.length - TaxColumns.TAXED_TOTAL.length - 1);
                    column.key = TaxColumns.TAXED_TOTAL;
                    column.format = ColumnFormats.CURRENCY;
                } else if (column.key.endsWith(TaxColumns.TAX)) {
                    column.keyValue = column.key.substring(0, column.key.length - TaxColumns.TAX.length - 1);
                    column.key = TaxColumns.TAX;
                    column.format = ColumnFormats.CURRENCY;
                }
            }

            let accessKey = formKey;

            if (!!column.aggregation) {
                accessKey = accessKey + '_' + column.aggregation;
            }
            if (column.format === ColumnFormats.PERCENT) {
                accessKey = accessKey + '_PERCENT';
            }
            column.accessKey = accessKey;
            this.close(column);
        }
    }

    requireCount() {
        return this.requireNumeric && (
            this.selectedColumnField.format === ColumnFormats.DATE ||
            this.selectedColumnField.format === ColumnFormats.HOUR ||
            this.selectedColumnField.format === ColumnFormats.MONTH ||
            this.selectedColumnField.format === ColumnFormats.YEAR ||
            this.selectedColumnField.key === 'DAY' ||
            this.selectedColumnField.key === 'WEEK' ||
            this.selectedColumnField.key.endsWith('_DAY') ||
            this.selectedColumnField.key.endsWith('_WEEK'));
    }
}
